<template>
  <b-container fluid>
    <b-overlay
      :show="isLoading"
      rounded="sm"
    >
      <b-card no-body>
        <b-card-header>
          <b-card-title>Danh sách hội đồng</b-card-title>
        </b-card-header>
        <b-card-body class="pb-1">
          <b-row>
            <b-col
              sm="12"
              md="6"
              lg="4"
              xl="4"
            >
              <b-form-group
                label="Năm học"
                label-for="year"
              >
                <v-select
                  id="year"
                  v-model="filter.yearId"
                  label="name"
                  :options="years"
                  :reduce="(option) => option.id"
                />
              </b-form-group>
            </b-col>
            <b-col
              sm="12"
              md="6"
              lg="8"
              xl="8"
            >
              <div class="text-right mt-1">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-1"
                  @click="getDataSourcesFromStore"
                >
                  <span class="text-nowrap text-right">
                    <feather-icon icon="SearchIcon" /> Tìm kiếm
                  </span>
                </b-button>
                <b-button
                  v-if="creatable"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  v-b-modal.reviewBoardSaveModal
                  variant="success"
                  @click="currentReviewBoard = undefined"
                >
                  <span class="text-nowrap text-right">
                    <feather-icon icon="PlusIcon" /> Thêm hội đồng
                  </span>
                </b-button>
              </div>
            </b-col>
          </b-row>
          <b-row :class="{'mt-2': creatable}">
            <b-col cols="12">
              <vue-good-table
                mode="remote"
                :columns="columns"
                :rows="dataSources"
                :pagination-options="paginationOptions"
                :total-rows="totalRows"
                :line-numbers="true"
                :row-style-class="rowStyleClassFn"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange"
                @on-column-filter="onColumnFilter"
                @on-per-page-change="onPerPageChange"
                @on-row-click="onRowClick"
              >
                <div
                  slot="emptystate"
                  style="text-align: center; font-weight: bold"
                >
                  Không có bản ghi nào !
                </div>
                <template
                  slot="table-row"
                  slot-scope="props"
                >
                  <span v-if="props.column.field === 'status'">
                    {{ getStatusName(props.row.status) }}
                  </span>

                  <span v-else-if="props.column.field === 'yearId'">
                    {{ getYear(props.row.yearId) }}
                  </span>

                  <span v-else-if="props.column.field === 'action'">
                    <b-dropdown
                      variant="link"
                      no-caret
                      :right="$store.state.appConfig.isRTL"
                    >
                      <template #button-content>
                        <feather-icon
                          icon="MoreVerticalIcon"
                          size="16"
                          class="align-middle text-body"
                        />
                      </template>
                      <b-dropdown-item
                        v-if="updatable"
                        v-b-modal.reviewBoardSaveModal
                        @click="onEditReviewBoard(props.row)"
                      >
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">Sửa</span>
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="reviewerCreatable"
                        v-b-modal.reviewerSaveModal
                        @click="onCreateReviewer(props.row)"
                      >
                        <feather-icon icon="PlusIcon" />
                        <span class="align-middle ml-50">Thêm thành viên</span>
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="deletable"
                        @click="onDeleteReviewBoard(props.row)"
                      >
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50">Xóa</span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </span>

                  <!-- Column: Common -->
                  <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>

                <!-- pagination -->
                <template
                  slot="pagination-bottom"
                  slot-scope="props"
                >
                  <div class="d-flex justify-content-between flex-wrap">
                    <div class="d-flex align-items-center mb-0 mt-1">
                      <span class="text-nowrap"> Hiển thị 1 đến </span>
                      <b-form-select
                        v-model="filter.itemsPerPage"
                        :options="itemsPerPageOptions"
                        class="mx-1"
                        @input="(value) => props.perPageChanged({ currentPerPage: value })"
                      />
                      <span class="text-nowrap">của {{ dataSources.length }} bản ghi</span>
                    </div>
                    <div>
                      <b-pagination
                        :value="1"
                        :total-rows="totalRows"
                        :per-page="filter.itemsPerPage"
                        class="mt-1 mb-0"
                        @input="(value) => props.pageChanged({ currentPage: value })"
                      />
                    </div>
                  </div>
                </template>
              </vue-good-table>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <ReviewBoardSave
        :years="years"
        :review-board="currentReviewBoard"
        @succeed="getDataSourcesFromStore"
      />

      <Reviewer
        :review-board-id="currentReviewBoard ? currentReviewBoard.id : null"
        :review-board-name="currentReviewBoard ? currentReviewBoard.name : ''"
        @onLoading="setLoading"
        @onUpdateReviewer="setReviewer"
      />

      <ReviewerSave
        :review-board-id="currentReviewBoard ? currentReviewBoard.id : null"
        :reviewer="currentReviewer"
        @succeed="onSucceedCreateReviewer"
      />
    </b-overlay>
  </b-container>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BContainer,
  BDropdown,
  BDropdownItem, BFormGroup,
  BFormSelect,
  BOverlay,
  BPagination,
  BRow,
  BCardTitle,
  BCardBody, BCardHeader,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { hasPermissionForResource, isSystemAdmin } from '@/utils'
import { PermissionCode, ResourceCode } from '@/const/code'
import vSelect from 'vue-select'
import { STATUSES } from '@/const/status'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { VueGoodTable } from 'vue-good-table'
import { getUser } from '@/auth/utils'
import ReviewBoardSave from '@/views/research/review-board/ReviewBoardSave.vue'
import Reviewer from '@/views/research/reviewer/Reviewer.vue'
import ReviewerSave from '@/views/research/reviewer/ReviewerSave.vue'

export default {
  name: 'ResearchReviewBoard',
  directives: {
    Ripple,
  },
  components: {
    ReviewerSave,
    Reviewer,
    ReviewBoardSave,
    vSelect,
    BFormGroup,
    BButton,
    BCard,
    BOverlay,
    BPagination,
    BFormSelect,
    BDropdownItem,
    BDropdown,
    BContainer,
    BRow,
    BCol,
    BCardHeader,
    BCardTitle,
    BCardBody,
    VueGoodTable,
  },
  data() {
    return {
      user: getUser(),
      isLoading: false,
      currentReviewBoard: undefined,
      filter: {
        organizationId: null,
        currentPage: 1,
        itemsPerPage: 20,
        yearId: null,
        name: null,
        status: null,
        sort: null,
      },
      paginationOptions: {
        enabled: true,
      },
      itemsPerPageOptions: [10, 20, 30, 50, 80, 100],
      currentReviewer: undefined,
    }
  },
  computed: {
    ...mapGetters({
      dataSources: 'reviewBoard/reviewBoards',
      totalRows: 'reviewBoard/totalRows',
      years: 'reviewBoard/years',
    }),
    columns() {
      return [
        {
          label: 'Tên hội đồng',
          field: 'name',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            placeholder: 'Nhập tên hội đồng',
          },
          sortable: true,
          thClass: 'text-center',
        },
        {
          label: 'Năm học',
          field: 'yearId',
          sortable: false,
          thClass: 'text-center',
        },
        {
          label: 'Ngày thành lập',
          field: 'establishDate',
          sortable: false,
          thClass: 'text-center',
        },
        {
          label: 'Số quyết định',
          field: 'establishDocNo',
          sortable: false,
          thClass: 'text-center',
        },
        {
          label: 'Trạng thái',
          field: 'status',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            filterDropdownItems: STATUSES.map(status => ({ value: status.value, text: status.label })),
            placeholder: 'Chọn trạng thái',
          },
          sortable: false,
          thClass: 'text-center',
        },
        {
          label: '',
          field: 'action',
          sortable: false,
          tdClass: 'text-center',
        },
      ]
    },
    creatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.CREATE, ResourceCode.RESEARCH_REVIEW_BOARD)
    },
    updatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.UPDATE, ResourceCode.RESEARCH_REVIEW_BOARD)
    },
    deletable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.DELETE, ResourceCode.RESEARCH_REVIEW_BOARD)
    },
    reviewerCreatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.CREATE, ResourceCode.RESEARCH_REVIEWER)
    },
  },
  async created() {
    this.updateParams({ organizationId: this.user.orgId })
    this.isLoading = true
    try {
      await this.getYears()
    } catch (e) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: `[${e.code}] ${e.message}`,
          icon: 'XCircleIcon',
          variant: 'danger',
        },
      })
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    ...mapActions({
      getDataSources: 'reviewBoard/getReviewBoards',
      getYears: 'reviewBoard/getYears',
      deleteReviewBoard: 'reviewBoard/deleteReviewBoard',
      getReviewers: 'reviewer/getReviewers',
    }),
    async onPageChange(param) {
      this.updateParams({ currentPage: param.currentPage })
      await this.getDataSourcesFromStore()
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps }
    },
    async getDataSourcesFromStore() {
      this.isLoading = true
      try {
        await this.getDataSources(this.filter)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `[${e.code}] ${e.message}`,
            icon: 'XCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    async onPerPageChange(param) {
      this.updateParams({ currentPage: 1, itemsPerPage: param.currentPerPage })
      await this.getDataSourcesFromStore()
    },
    async onSortChange(param) {
      if (param.length > 0) {
        const { field, type } = param[0]
        if (field === 'name') this.updateParams({ sort: `1_${type}` })
      }
      await this.getDataSourcesFromStore()
    },
    async onColumnFilter(param) {
      const { columnFilters } = param
      if (Object.hasOwn(columnFilters, 'name')) this.updateParams({ name: columnFilters.name })
      if (Object.hasOwn(columnFilters, 'status')) this.updateParams({ status: columnFilters.status })
      await this.getDataSourcesFromStore()
    },
    onRowClick(param) {
      const { row } = param
      this.setCurrentReviewBoard(row)
    },
    getStatusName(id) {
      return STATUSES.find(status => status.value === id).label
    },
    getYear(id) {
      return this.years.find(year => year.id === id).name
    },
    onEditReviewBoard(reviewBoard) {
      this.setCurrentReviewBoard(reviewBoard)
    },
    onCreateReviewer(reviewBoard) {
      this.setCurrentReviewBoard(reviewBoard)
      this.currentReviewer = undefined
    },
    setCurrentReviewBoard(reviewBoard) {
      const {
        rowNum,
        originalIndex,
        vgt_id,
        ...rest
      } = reviewBoard
      this.currentReviewBoard = rest
    },
    onDeleteReviewBoard(reviewBoard) {
      this.$swal({
        title: `Bạn chắc chắn muốn xóa hội đồng <span class="text-danger">${reviewBoard.name}</span>?`,
        text: 'Bạn sẽ không thể khôi phục lại dữ liệu này!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '<i class="fa-solid fa-check"></i> Đồng ý',
        cancelButtonText: '<i class="fa-solid fa-times"></i> Hủy',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          this.isLoading = true
          try {
            const response = await this.deleteReviewBoard(reviewBoard.id)
            if (response) {
              const { isSuccessful, message } = response
              if (isSuccessful) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: message,
                    icon: 'CheckIcon',
                    variant: 'success',
                  },
                })
                try {
                  await this.getDataSources(this.filter)
                } catch (e) {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: `[${e.code}] ${e.message}`,
                      icon: 'XCircleIcon',
                      variant: 'danger',
                    },
                  })
                }
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: message,
                    icon: 'XCircleIcon',
                    variant: 'danger',
                  },
                })
              }
            }
          } catch (e) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: `[${e.code}] ${e.message}`,
                icon: 'XCircleIcon',
                variant: 'danger',
              },
            })
          } finally {
            this.isLoading = false
          }
        }
      })
    },
    setLoading(value) {
      this.isLoading = value
    },
    async onSucceedCreateReviewer() {
      this.isLoading = true
      try {
        await this.getReviewers({ reviewBoardId: this.currentReviewBoard.id })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `[${e.code}] ${e.message}`,
            icon: 'XCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    setReviewer(value) {
      this.currentReviewer = value
    },
    rowStyleClassFn(row) {
      return this.currentReviewBoard && row.id === this.currentReviewBoard.id ? 'row-selected' : ''
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.row-selected {
  background-color: rgba(251, 205, 68, 0.89);
  color: #FFF;
}
</style>
